import React, { useEffect, useState } from "react";
import { Card, Typography, CircularProgress, Box, useTheme, alpha } from "@mui/material";
import { setFilterCardStatus, setHasFilter } from "app/store/statusSlice";
import { useDispatch, useSelector } from "react-redux";

import FiberNewOutlinedIcon from "@mui/icons-material/FiberNewOutlined";
import PauseCircleOutlinedIcon from "@mui/icons-material/PauseCircleOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";


const STATUS_THEME = {
  NEW: { accent: "#4C6FFF", icon: FiberNewOutlinedIcon },
  HOLD: { accent: "#F5921B", icon: PauseCircleOutlinedIcon },
  IN_PROGRESS: { accent: "#2C9FE0", icon: PendingActionsOutlinedIcon },
  COMPLETED: { accent: "#22B07D", icon: TaskAltOutlinedIcon },
};


const getCardColors = (accent, mode) => ({
  cardBg: alpha(accent, mode === "dark" ? 0.16 : 0.07),
  iconBg: alpha(accent, mode === "dark" ? 0.32 : 0.15),
});

export default function Cards(props) {
  const { setOrderStatus, orderCount } = props;
  const theme = useTheme();
  const [news, setNews] = useState(null);
  const [inProgress, setInProgress] = useState(null);
  const [completed, setCompleted] = useState(null);
  const [hold, setHold] = useState(null);
  const dispatch = useDispatch();
  const activeStatus = useSelector(({ status }) => status.cardStatus);

  useEffect(() => {
    if (orderCount) {
      const newCount = orderCount.find((item) => item._id === "NEW");
      setNews(newCount?.count ? newCount?.count : 0);
      const inProgressCount = orderCount.find(
        (item) => item._id === "IN_PROGRESS"
      );
      setInProgress(inProgressCount?.count ? inProgressCount?.count : 0);
      const completedCount = orderCount.find(
        (item) => item._id === "COMPLETED"
      );
      setCompleted(completedCount?.count ? completedCount?.count : 0);
      const holdCount = orderCount.find(
        (item) => item._id === "HOLD"
      );
      setHold(holdCount?.count ? holdCount?.count : 0);
    } else {
      setNews(null);
      setInProgress(null);
      setCompleted(null);
      setHold(null);
    }
  }, [orderCount]);

  const handleCardClick = (status) => {
    setOrderStatus(status);
    dispatch(setFilterCardStatus(status));
    dispatch(setHasFilter(true));
  };

  const renderCard = (status, label, count) => {
    const isActive = status === activeStatus;
    const { accent, icon: Icon } = STATUS_THEME[status];
    const { cardBg, iconBg } = getCardColors(accent, theme.palette.mode);

    return (
      <div className="w-full sm:w-1/4 p-4">
        <Card
          onClick={() => handleCardClick(status)}
          elevation={0}
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            p: 2,
            borderRadius: 3,
            cursor: "pointer",
            overflow: "hidden",
            bgcolor: cardBg,
            borderLeft: `4px solid ${accent}`,
            boxShadow: isActive
              ? `0 0 0 1.5px ${accent}, 0 6px 14px rgba(16,24,40,0.12)`
              : "0 1px 4px rgba(16,24,40,0.06)",
            transform: isActive ? "translateY(-1px)" : "none",
            transition: "transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease",
            "&:hover": {
              boxShadow: `0 0 0 1.5px ${accent}, 0 6px 14px rgba(16,24,40,0.1)`,
            },
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              width: 40,
              height: 40,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: iconBg,
            }}
          >
            <Icon sx={{ fontSize: 20, color: accent }} />
          </Box>

          <div>
            <Typography sx={{ fontSize: 12, fontWeight: 500, color: "text.secondary" }}>
              {label}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontSize: 20,
                fontWeight: 700,
                minHeight: 27,
                display: "flex",
                alignItems: "center",
              }}
            >
              {count === null ? <CircularProgress size={18} sx={{ color: accent }} /> : count}
            </Typography>
          </div>
        </Card>
      </div>
    );
  };

  return (
    <>
      {renderCard("NEW", "New", news)}
      {renderCard("HOLD", "Hold", hold)}
      {renderCard("IN_PROGRESS", "In Progress", inProgress)}
      {renderCard("COMPLETED", "Completed", completed)}
    </>
  );
}